"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ChevronDown, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";

const navLinks = [
  { name: "Product", href: "/platform" },
  { name: "Connect AI", href: "/platform/mcp" },
  { name: "Pricing", href: "/pricing" },
  { name: "Company", href: "/company" },
];

const resourceLinks = [
  { name: "Compare", href: "/resources/comparison" },
  { name: "Use Cases", href: "/resources/use-cases" },
  { name: "Guides", href: "/resources/guides" },
  { name: "Free Tools", href: "/resources" },
  { name: "Blog", href: "/resources/blog" },
];

export function WebsiteNavbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const isResourcesActive = pathname.startsWith("/resources");

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b border-slate-800 bg-slate-950/80 backdrop-blur-md">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2 group">
              <Image 
                src="/logo.png" 
                alt="Storita Logo" 
                width={32} 
                height={32} 
                className="transition-transform group-hover:scale-110"
              />
              <span className="text-xl font-bold tracking-tight">Storita</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={cn(
                    "text-base font-medium transition-all hover:text-purple-400 relative group/link",
                    pathname === link.href ? "text-purple-400" : "text-slate-400"
                  )}
                >
                  {link.name}
                  <span className={cn(
                    "absolute -bottom-1 left-0 w-0 h-0.5 bg-purple-500 transition-all group-hover/link:w-full",
                    pathname === link.href ? "w-full" : "w-0"
                  )} />
                </Link>
              ))}

              <div className="relative group/resources">
                <div
                  className={cn(
                    "flex items-center gap-1 text-base font-medium transition-all hover:text-purple-400 relative group/link",
                    isResourcesActive ? "text-purple-400" : "text-slate-400"
                  )}
                >
                  <Link href="/resources">Resources</Link>
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover/resources:rotate-180" />
                  <span className={cn(
                    "absolute -bottom-1 left-0 w-0 h-0.5 bg-purple-500 transition-all group-hover/link:w-full",
                    isResourcesActive ? "w-full" : "w-0"
                  )} />
                </div>

                <div className="pointer-events-none absolute left-1/2 top-full z-20 w-56 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover/resources:pointer-events-auto group-hover/resources:opacity-100 group-focus-within/resources:pointer-events-auto group-focus-within/resources:opacity-100">
                  <div className="rounded-2xl border border-slate-800 bg-slate-950/95 p-2 shadow-2xl shadow-black/30 backdrop-blur-xl">
                    {resourceLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        className="block rounded-xl px-4 py-3 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-900 hover:text-white"
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                href="/login"
                className="text-base font-medium text-slate-400 transition-colors hover:text-slate-50"
              >
                Sign In
              </Link>
              <WebsiteCtaLink href="/login">Start Now</WebsiteCtaLink>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-slate-400 hover:text-slate-50"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-b border-slate-800 bg-slate-950 px-4 py-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={cn(
                  "block text-base font-medium transition-colors",
                  pathname === link.href ? "text-purple-400" : "text-slate-400"
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="space-y-3">
              <div className={cn("text-base font-medium", isResourcesActive ? "text-purple-400" : "text-slate-400")}>
                Resources
              </div>
              <div className="pl-4 space-y-3 border-l border-slate-800">
                {resourceLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    className="block text-sm text-slate-500 transition-colors hover:text-slate-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="pt-4 border-t border-slate-800 flex flex-col space-y-4">
              <Link
                href="/login"
                className="w-full text-center text-base font-medium text-slate-400 transition-colors hover:text-slate-50"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign In
              </Link>
              <WebsiteCtaLink href="/login" fullWidth onClick={() => setIsMenuOpen(false)}>
                Start Now
              </WebsiteCtaLink>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
